import React, { useCallback, useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Plus, LayoutGrid, Check, Cloud, ChevronDown, Pin, Star, Trash2, BarChart2, Map, Type, Table } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { ChartWidget } from '../components/workspace/ChartWidget';
import { MapWidget } from '../components/workspace/MapWidget';
import { TextWidget } from '../components/workspace/TextWidget';
import { TableWidget } from '../components/workspace/TableWidget';
import { ModelRunSelector } from '../components/workspace/ModelRunSelector';
import type { ModelOverride } from '../components/workspace/ModelRunSelector';
import { LocationSelector } from '../components/workspace/LocationSelector';
import type { LocationOverride } from '../components/workspace/LocationSelector';
import { useWorkspaces, type Widget, type Workspace } from '../hooks/useWorkspaces';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
const ResponsiveGridLayout = WidthProvider(Responsive);
function WorkspaceListItem({
  workspace,
  isActive,
  onSelect,
  onPin,
  onFavorite,
  onDelete,
  onUpdateDescription,
  canDelete
}: {
  workspace: Workspace;
  isActive: boolean;
  onSelect: () => void;
  onPin: (e: React.MouseEvent) => void;
  onFavorite: (e: React.MouseEvent) => void;
  onDelete: () => void;
  onUpdateDescription: (id: string, desc: string) => void;
  canDelete: boolean;
}) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditingDesc, setIsEditingDesc] = useState(false);
  const [descValue, setDescValue] = useState(workspace.description);
  useEffect(() => {
    setIsDeleting(false);
    setIsEditingDesc(false);
    setDescValue(workspace.description);
  }, [workspace.id, workspace.description]);
  const handleDescSave = () => {
    onUpdateDescription(workspace.id, descValue);
    setIsEditingDesc(false);
  };
  const handleDescKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleDescSave();
    } else if (e.key === 'Escape') {
      setDescValue(workspace.description);
      setIsEditingDesc(false);
    }
  };
  if (isDeleting) {
    return <div className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-red-500/10 border border-red-500/20" data-id="element-2810">
        <span className="text-sm font-medium text-red-400" data-id="element-2811">
          Delete workspace?
        </span>
        <div className="flex items-center gap-2" data-id="element-2812">
          <button onClick={e => {
          e.stopPropagation();
          setIsDeleting(false);
        }} className="text-xs text-white/60 hover:text-white px-2 py-1 rounded hover:bg-white/10" data-id="element-2813">
            Cancel
          </button>
          <button onClick={e => {
          e.stopPropagation();
          onDelete();
        }} className="text-xs bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600 shadow-sm" data-id="element-2814">
            Delete
          </button>
        </div>
      </div>;
  }
  return <div onClick={onSelect} className={`group flex items-center justify-between px-3 py-2.5 rounded-lg cursor-pointer transition-colors relative ${isActive ? 'bg-white/[0.08]' : 'hover:bg-white/[0.04]'}`} data-id="element-2815">
      {isActive && <div className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-0.5 bg-[#EC4899] rounded-r-full" data-id="element-2816" />}
      <div className="flex-1 min-w-0 pr-3" data-id="element-2817">
        <div className="flex items-center gap-2" data-id="element-2818">
          <span className={`text-sm font-medium truncate ${isActive ? 'text-white' : 'text-white/80'}`} data-id="element-2819">
            {workspace.name}
          </span>
        </div>

        {isActive && isEditingDesc ? <div className="mt-1" onClick={e => e.stopPropagation()} data-id="element-2820">
            <input autoFocus value={descValue} onChange={e => setDescValue(e.target.value)} onBlur={handleDescSave} onKeyDown={handleDescKeyDown} className="w-full bg-[#0B1120] text-xs text-white border border-slate-600 rounded px-1.5 py-0.5 focus:outline-none focus:border-[#EC4899]" data-id="element-2821" />
          </div> : <p className={`text-xs text-white/40 truncate mt-0.5 ${isActive ? 'cursor-text hover:text-white/60' : ''}`} onClick={e => {
        if (isActive) {
          e.stopPropagation();
          setIsEditingDesc(true);
        }
      }} title={isActive ? 'Click to edit description' : undefined} data-id="element-2822">
            {workspace.description || 'No description'}
          </p>}
      </div>
      <div className="flex items-center gap-1" data-id="element-2823">
        <button onClick={onPin} className={`p-1.5 rounded-md transition-all ${workspace.pinned ? 'text-[#EC4899] opacity-100' : 'text-white/30 opacity-0 group-hover:opacity-100 hover:text-white hover:bg-white/[0.1]'}`} title={workspace.pinned ? 'Unpin' : 'Pin'} data-id="element-2824">
          <Pin size={12} className={workspace.pinned ? 'fill-current' : ''} data-id="element-2825" />
        </button>
        <button onClick={onFavorite} className={`p-1.5 rounded-md transition-all ${workspace.favorited ? 'text-amber-400 opacity-100' : 'text-white/30 opacity-0 group-hover:opacity-100 hover:text-white hover:bg-white/[0.1]'}`} title={workspace.favorited ? 'Remove from favorites' : 'Add to favorites'} data-id="element-2826">
          <Star size={12} className={workspace.favorited ? 'fill-current' : ''} data-id="element-2827" />
        </button>
        {canDelete && <button onClick={e => {
        e.stopPropagation();
        setIsDeleting(true);
      }} className="p-1.5 rounded-md transition-all text-white/30 opacity-0 group-hover:opacity-100 hover:text-red-400 hover:bg-red-500/10" title="Delete workspace" data-id="element-2828">
            <Trash2 size={12} data-id="element-2829" />
          </button>}
      </div>
    </div>;
}
export function WorkspacesView() {
  const {
    id: urlWorkspaceId
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    workspaces,
    activeWorkspaceId,
    activeWorkspace,
    saveStatus,
    setActiveWorkspace,
    createWorkspace,
    deleteWorkspace,
    renameWorkspace,
    updateDescription,
    togglePin,
    toggleFavorite,
    updateWidgets
  } = useWorkspaces();
  // Handle create workspace from TopBar navigation
  useEffect(() => {
    if ((location.state as any)?.renaming) {
      navigate(`/workspace/${activeWorkspaceId}`, {
        replace: true,
        state: {}
      });
      setTimeout(() => {
        setIsRenaming(true);
        setRenameValue('Untitled Workspace');
      }, 100);
    }
  }, [location.state, navigate, activeWorkspaceId]);
  // Sync active workspace from URL param
  useEffect(() => {
    if (urlWorkspaceId && urlWorkspaceId !== activeWorkspaceId) {
      const exists = workspaces.find(w => w.id === urlWorkspaceId);
      if (exists) {
        setActiveWorkspace(urlWorkspaceId);
      }
    }
  }, [urlWorkspaceId, workspaces, activeWorkspaceId, setActiveWorkspace]);
  const [widgets, setWidgets] = useState<Widget[]>(activeWorkspace?.widgets || []);
  const [counter, setCounter] = useState(activeWorkspace?.counter || 0);
  // Sync local widget state whenever the active workspace changes
  const lastSyncedIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (activeWorkspaceId !== lastSyncedIdRef.current) {
      lastSyncedIdRef.current = activeWorkspaceId;
      const ws = workspaces.find(w => w.id === activeWorkspaceId);
      if (ws) {
        setWidgets(ws.widgets || []);
        setCounter(ws.counter || 0);
        setBackgroundMapId(null);
      }
    }
  }, [activeWorkspaceId, workspaces]);
  const [workspaceMenuOpen, setWorkspaceMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'all' | 'pinned' | 'favorites'>('all');
  const titleRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const titleAreaRef = useRef<HTMLDivElement>(null);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [modelOverride, setModelOverride] = useState<ModelOverride | null>(null);
  const [locationOverride, setLocationOverride] = useState<LocationOverride | null>(null);
  const [backgroundMapId, setBackgroundMapId] = useState<string | null>(null);
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const renameInputRef = useRef<HTMLInputElement>(null);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const addMenuRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [useCompactAdd, setUseCompactAdd] = useState(false);
  const useCompactAddRef = useRef(false);
  const collapseWidthRef = useRef<number | null>(null); // width at which we last collapsed
  // Collapse add buttons into dropdown when toolbar content would overflow.
  // Uses hysteresis: collapse when overflowing, only re-expand when there's
  // 60px+ of extra room beyond the collapse point — prevents oscillation.
  useEffect(() => {
    const el = toolbarRef.current;
    if (!el) return;
    const measure = () => {
      const clientWidth = el.clientWidth;
      if (!useCompactAddRef.current) {
        // Currently expanded — collapse if content overflows
        if (el.scrollWidth > clientWidth) {
          collapseWidthRef.current = clientWidth;
          useCompactAddRef.current = true;
          setUseCompactAdd(true);
        }
      } else {
        // Currently compact — only expand if we have clear room beyond collapse point
        const threshold = (collapseWidthRef.current ?? 0) + 60;
        if (clientWidth > threshold) {
          collapseWidthRef.current = null;
          useCompactAddRef.current = false;
          setUseCompactAdd(false);
        }
      }
    };
    const t = setTimeout(measure, 0);
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => {
      observer.disconnect();
      clearTimeout(t);
    };
  }, []);
  // No-op trigger save — persistence is handled by the hook
  const triggerSave = useCallback(() => {}, []);
  const handleLayoutChange = useCallback((layout: any) => {
    if (!layout || layout.length === 0) return;
    setWidgets(currentWidgets => {
      const updatedWidgets = currentWidgets.map(w => {
        const layoutItem = layout.find((l: any) => l.i === w.i);
        if (layoutItem) {
          return {
            ...w,
            x: layoutItem.x,
            y: layoutItem.y,
            w: layoutItem.w,
            h: layoutItem.h
          };
        }
        return w;
      });
      const hasChanged = currentWidgets.some((w, idx) => {
        const u = updatedWidgets[idx];
        return u && (w.x !== u.x || w.y !== u.y || w.w !== u.w || w.h !== u.h);
      });
      if (hasChanged) {
        updateWidgets(updatedWidgets, counter);
        return updatedWidgets;
      }
      return currentWidgets;
    });
  }, [counter, updateWidgets]);
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (titleAreaRef.current && !titleAreaRef.current.contains(e.target as Node) && menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setWorkspaceMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const addWidget = (type: 'chart' | 'map' | 'text' | 'table') => {
    const newCounter = counter + 1;
    const cols = 12;
    // Preferred widths per type — NOT full width
    const preferredW: Record<string, number> = {
      chart: 6,
      map: 6,
      table: 6,
      text: 4
    };
    const preferredH: Record<string, number> = {
      chart: 5,
      map: 6,
      table: 5,
      text: 3
    };
    const wantW = preferredW[type] || 6;
    const wantH = preferredH[type] || 5;
    const visibleWidgets = widgets.filter(w => w.i !== backgroundMapId);
    let newX = 0;
    let newY = 0;
    let newW = wantW;
    if (visibleWidgets.length > 0) {
      // Build an occupancy grid to find the first gap that fits
      let maxBottom = 0;
      visibleWidgets.forEach(w => {
        const bottom = w.y + w.h;
        if (bottom > maxBottom) maxBottom = bottom;
      });
      // Scan row by row for a gap that fits wantW columns
      let placed = false;
      for (let row = 0; row <= maxBottom && !placed; row++) {
        // Find widgets that occupy this row
        const rowWidgets = visibleWidgets.filter(w => w.y <= row && w.y + w.h > row);
        // Build occupied columns for this row
        const occupied = new Set<number>();
        rowWidgets.forEach(w => {
          for (let c = w.x; c < w.x + w.w; c++) {
            occupied.add(c);
          }
        });
        // Scan for a contiguous free span of wantW columns
        for (let startCol = 0; startCol <= cols - wantW; startCol++) {
          let fits = true;
          for (let c = startCol; c < startCol + wantW; c++) {
            if (occupied.has(c)) {
              fits = false;
              break;
            }
          }
          if (fits) {
            // Verify the full height block is free too
            let heightFits = true;
            for (let r = row; r < row + wantH && heightFits; r++) {
              const rWidgets = visibleWidgets.filter(w => w.y <= r && w.y + w.h > r);
              for (let c = startCol; c < startCol + wantW; c++) {
                const blocked = rWidgets.some(w => c >= w.x && c < w.x + w.w);
                if (blocked) {
                  heightFits = false;
                  break;
                }
              }
            }
            if (heightFits) {
              newX = startCol;
              newY = row;
              newW = wantW;
              placed = true;
              break;
            }
          }
        }
      }
      // No gap found — place below everything at preferred width
      if (!placed) {
        newX = 0;
        newY = maxBottom;
        newW = wantW;
      }
    }
    const newWidget: Widget = {
      i: `${type}-${newCounter}`,
      x: newX,
      y: newY,
      w: newW,
      h: wantH,
      type
    };
    const newWidgets = [...widgets, newWidget];
    setWidgets(newWidgets);
    setCounter(newCounter);
    updateWidgets(newWidgets, newCounter);
  };
  const removeWidget = (id: string) => {
    if (backgroundMapId === id) setBackgroundMapId(null);
    const newWidgets = widgets.filter(w => w.i !== id);
    setWidgets(newWidgets);
    updateWidgets(newWidgets, counter);
  };
  const expandWidget = useCallback((widgetId: string) => {
    const COLS = 12;
    const widget = widgets.find(w => w.i === widgetId);
    if (!widget) return;
    // Find all widgets overlapping the same row
    const peers = widgets.filter(w => w.i !== widgetId && w.y < widget.y + widget.h && w.y + w.h > widget.y);
    // Build occupied column set from peers
    const occupied = new Set<number>();
    peers.forEach(w => {
      for (let c = w.x; c < w.x + w.w; c++) occupied.add(c);
    });
    // Expand the free range around the widget's current position
    let rangeStart = widget.x;
    while (rangeStart > 0 && !occupied.has(rangeStart - 1)) rangeStart--;
    let rangeEnd = widget.x + widget.w;
    while (rangeEnd < COLS && !occupied.has(rangeEnd)) rangeEnd++;
    // Already filling all available space — no change
    if (rangeEnd - rangeStart === widget.w && rangeStart === widget.x) return;
    const updated = widgets.map(w => w.i === widgetId ? {
      ...w,
      x: rangeStart,
      w: rangeEnd - rangeStart
    } : w);
    setWidgets(updated);
    updateWidgets(updated, counter);
  }, [widgets, counter, updateWidgets]);
  const duplicateWidget = (id: string) => {
    const source = widgets.find(w => w.i === id);
    if (!source) return;
    const newCounter = counter + 1;
    const cols = 12;
    const visibleWidgets = widgets.filter(w => w.i !== backgroundMapId);
    const wantW = source.w;
    const wantH = source.h;
    let newX = 0;
    let newY = 0;
    let newW = wantW;
    if (visibleWidgets.length > 0) {
      let maxBottom = 0;
      visibleWidgets.forEach(w => {
        const bottom = w.y + w.h;
        if (bottom > maxBottom) maxBottom = bottom;
      });
      let placed = false;
      for (let row = 0; row <= maxBottom && !placed; row++) {
        const rowWidgets = visibleWidgets.filter(w => w.y <= row && w.y + w.h > row);
        const occupied = new Set<number>();
        rowWidgets.forEach(w => {
          for (let c = w.x; c < w.x + w.w; c++) {
            occupied.add(c);
          }
        });
        for (let startCol = 0; startCol <= cols - wantW; startCol++) {
          let fits = true;
          for (let c = startCol; c < startCol + wantW; c++) {
            if (occupied.has(c)) {
              fits = false;
              break;
            }
          }
          if (fits) {
            let heightFits = true;
            for (let r = row; r < row + wantH && heightFits; r++) {
              const rWidgets = visibleWidgets.filter(w => w.y <= r && w.y + w.h > r);
              for (let c = startCol; c < startCol + wantW; c++) {
                const blocked = rWidgets.some(w => c >= w.x && c < w.x + w.w);
                if (blocked) {
                  heightFits = false;
                  break;
                }
              }
            }
            if (heightFits) {
              newX = startCol;
              newY = row;
              newW = wantW;
              placed = true;
              break;
            }
          }
        }
      }
      if (!placed) {
        newX = 0;
        newY = maxBottom;
        newW = wantW;
      }
    }
    const newWidget: Widget = {
      i: `${source.type}-${newCounter}`,
      x: newX,
      y: newY,
      w: newW,
      h: wantH,
      type: source.type
    };
    const newWidgets = [...widgets, newWidget];
    setWidgets(newWidgets);
    setCounter(newCounter);
    updateWidgets(newWidgets, newCounter);
  };
  const handleCreateWorkspace = () => {
    const newId = createWorkspace();
    setWorkspaceMenuOpen(false);
    navigate(`/workspace/${newId}`, {
      state: {
        renaming: true
      }
    });
  };
  const handleRenameSave = () => {
    if (renameValue.trim()) {
      renameWorkspace(activeWorkspaceId, renameValue.trim());
    }
    setIsRenaming(false);
  };
  const handleRenameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleRenameSave();
    } else if (e.key === 'Escape') {
      setIsRenaming(false);
      setRenameValue(activeWorkspace?.name || '');
    }
  };
  const filteredWorkspaces = workspaces.filter(w => {
    if (activeTab === 'pinned') return w.pinned;
    if (activeTab === 'favorites') return w.favorited;
    return true;
  });
  const getMenuPosition = () => {
    if (!titleRef.current) return {
      top: 0,
      left: 0
    };
    const rect = titleRef.current.getBoundingClientRect();
    const menuWidth = 320;
    const viewportWidth = window.innerWidth;
    const idealLeft = rect.left;
    const clampedLeft = Math.min(idealLeft, viewportWidth - menuWidth - 8);
    return {
      top: rect.bottom + 8,
      left: Math.max(8, clampedLeft)
    };
  };
  if (!activeWorkspace) return null;
  return <div className="jua-canvas jua-page-bg h-[calc(100vh-64px)] w-full flex flex-col overflow-hidden" data-id="element-2830">
      {/* Toolbar */}
      <div ref={toolbarRef} className="app-toolbar border-b border-slate-800 bg-[#0F172A] px-3 md:px-6 py-2 flex items-center gap-x-2 flex-shrink-0 overflow-hidden" data-id="element-2831">
        {/* Left: title + add buttons */}
        <div className="flex items-center gap-2 md:gap-4 min-w-0 flex-shrink-0" data-id="element-2832">
          <div className="flex items-center gap-1 flex-shrink-0" ref={titleAreaRef} data-id="element-2833">
            <button ref={titleRef} onClick={() => setWorkspaceMenuOpen(!workspaceMenuOpen)} className="p-1.5 rounded-lg hover:bg-white/[0.06] transition-colors text-[#EC4899] flex-shrink-0" data-id="element-2834">
              <LayoutGrid size={20} data-id="element-2835" />
            </button>

            {isRenaming ? <Input autoFocus data-rename-input value={renameValue} onChange={e => setRenameValue(e.target.value)} onBlur={handleRenameSave} onKeyDown={handleRenameKeyDown} className="h-8 w-32 sm:w-48 md:w-64 bg-transparent border-slate-600 text-sm sm:text-base md:text-lg font-semibold text-white px-2 focus:ring-0 focus:outline-none focus:border-slate-500" wrapperClassName="w-32 sm:w-48 md:w-64" data-id="element-2836" /> : <>
                <h1 className="text-sm sm:text-base md:text-lg font-semibold text-white flex items-center gap-1 cursor-pointer hover:text-white/80 transition-colors px-1 py-1 rounded-lg hover:bg-white/[0.04] min-w-0" onClick={() => setWorkspaceMenuOpen(!workspaceMenuOpen)} onDoubleClick={() => {
              setWorkspaceMenuOpen(false);
              setIsRenaming(true);
              setRenameValue(activeWorkspace.name);
            }} title="Click to open menu · Double click to rename" data-id="element-2837">
                  <span className="truncate max-w-[80px] sm:max-w-[140px] md:max-w-[260px]" data-id="element-2838">
                    {activeWorkspace.name}
                  </span>
                  <button onClick={e => {
                e.stopPropagation();
                setWorkspaceMenuOpen(!workspaceMenuOpen);
              }} className="flex-shrink-0" data-id="element-2839">
                    <ChevronDown size={14} className={`text-white/40 hover:text-white/80 transition-transform duration-200 ${workspaceMenuOpen ? 'rotate-180' : ''}`} data-id="element-2840" />
                  </button>
                </h1>

                {/* Workspace list dropdown */}
                {workspaceMenuOpen && ReactDOM.createPortal(<div ref={menuRef} className="fixed w-[320px] rounded-xl shadow-2xl shadow-black/50 border border-white/[0.08] overflow-hidden" style={{
              top: getMenuPosition().top,
              left: getMenuPosition().left,
              zIndex: 9999,
              background: 'rgba(15, 23, 42, 0.96)',
              backdropFilter: 'blur(40px) saturate(180%)',
              WebkitBackdropFilter: 'blur(40px) saturate(180%)'
            }} data-id="element-2841">
                      <div className="absolute inset-0 rounded-xl pointer-events-none" style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, transparent 50%, rgba(255,255,255,0.02) 100%)'
              }} data-id="element-2842" />

                      {/* Header */}
                      <div className="relative flex items-center justify-between px-3 pt-3 pb-2 border-b border-white/[0.06]" data-id="element-2843">
                        <div className="flex gap-1" data-id="element-2844">
                          {(['all', 'pinned', 'favorites'] as const).map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors capitalize ${activeTab === tab ? 'bg-white/[0.1] text-white' : 'text-white/40 hover:text-white/70 hover:bg-white/[0.05]'}`} data-id="element-2845">
                                {tab}
                              </button>)}
                        </div>
                        <button onClick={handleCreateWorkspace} className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium text-[#EC4899] hover:bg-[#EC4899]/10 transition-colors" data-id="element-2846">
                          <Plus size={11} data-id="element-2847" /> New
                        </button>
                      </div>

                      {/* Workspace list */}
                      <div className="relative max-h-[320px] overflow-y-auto p-1.5 space-y-0.5" data-id="element-2848">
                        {filteredWorkspaces.length === 0 ? <div className="py-6 text-center text-xs text-white/30" data-id="element-2849">
                            No workspaces in this view
                          </div> : filteredWorkspaces.map(ws => <WorkspaceListItem key={ws.id} workspace={ws} isActive={ws.id === activeWorkspaceId} onSelect={() => {
                  setActiveWorkspace(ws.id);
                  navigate(`/workspace/${ws.id}`);
                  setWorkspaceMenuOpen(false);
                }} onPin={e => {
                  e.stopPropagation();
                  togglePin(ws.id);
                }} onFavorite={e => {
                  e.stopPropagation();
                  toggleFavorite(ws.id);
                }} onDelete={() => deleteWorkspace(ws.id)} onUpdateDescription={updateDescription} canDelete={workspaces.length > 1} data-id="element-2850" />)}
                      </div>

                      {/* Footer actions */}
                      <div className="relative border-t border-white/[0.06] px-3 py-2 flex items-center justify-between" data-id="element-2851">
                        <button onClick={() => {
                  setWorkspaceMenuOpen(false);
                  setIsRenaming(true);
                  setRenameValue(activeWorkspace.name);
                }} className="text-[11px] text-white/40 hover:text-white/70 transition-colors" data-id="element-2852">
                          Rename current
                        </button>
                        <button onClick={() => {
                  navigate('/workspaces');
                  setWorkspaceMenuOpen(false);
                }} className="text-[11px] text-white/40 hover:text-white/70 transition-colors flex items-center gap-1" data-id="element-2853">
                          <LayoutGrid size={10} data-id="element-2854" /> View all
                        </button>
                      </div>
                    </div>, document.body)}
              </>}
          </div>

          <div className="h-6 w-px bg-slate-700 flex-shrink-0" data-id="element-2855" />

          {/* Compact Add dropdown */}
          {useCompactAdd && <div className="flex relative flex-shrink-0" ref={addMenuRef} data-id="element-2856">
              <Button variant="secondary" size="sm" className="h-8 gap-1.5" onClick={() => setAddMenuOpen(!addMenuOpen)} data-id="element-2857">
                <Plus size={14} data-id="element-2858" />
                <span data-id="element-2859">Add</span>
                <ChevronDown size={12} className={`transition-transform ${addMenuOpen ? 'rotate-180' : ''}`} data-id="element-2860" />
              </Button>
              {addMenuOpen && ReactDOM.createPortal(<div className="fixed w-40 rounded-lg bg-[#0F172A] border border-slate-700/60 shadow-xl z-[9999] overflow-hidden" style={{
            top: addMenuRef.current ? addMenuRef.current.getBoundingClientRect().bottom + 4 : 0,
            left: addMenuRef.current ? addMenuRef.current.getBoundingClientRect().left : 0
          }} data-id="element-2861">
                    {[{
              type: 'chart' as const,
              icon: BarChart2,
              label: 'Chart'
            }, {
              type: 'map' as const,
              icon: Map,
              label: 'Map'
            }, {
              type: 'text' as const,
              icon: Type,
              label: 'Text'
            }, {
              type: 'table' as const,
              icon: Table,
              label: 'Table'
            }].map(({
              type,
              icon: Icon,
              label
            }) => <button key={type} onClick={() => {
              addWidget(type);
              setAddMenuOpen(false);
            }} className="flex items-center gap-2.5 w-full px-3 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-white/[0.06] transition-colors" data-id="element-2862">
                        <Icon size={14} className="text-slate-400" data-id="element-2863" />
                        {label}
                      </button>)}
                  </div>, document.body)}
            </div>}

          {/* Expanded individual add buttons */}
          {!useCompactAdd && <div className="flex items-center gap-2 flex-shrink-0" data-id="element-2864">
              <Button variant="secondary" size="sm" className="h-8 gap-2" onClick={() => addWidget('chart')} data-id="element-2865">
                <Plus size={14} data-id="element-2866" /> Add Chart
              </Button>
              <Button variant="secondary" size="sm" className="h-8 gap-2" onClick={() => addWidget('map')} data-id="element-2867">
                <Plus size={14} data-id="element-2868" /> Add Map
              </Button>
              <Button variant="secondary" size="sm" className="h-8 gap-2" onClick={() => addWidget('text')} data-id="element-2869">
                <Plus size={14} data-id="element-2870" /> Add Text
              </Button>
              <Button variant="secondary" size="sm" className="h-8 gap-2" onClick={() => addWidget('table')} data-id="element-2871">
                <Plus size={14} data-id="element-2872" /> Add Table
              </Button>
            </div>}
        </div>

        {/* Right: selectors + save — wraps to second line on mobile */}
        <div className="flex items-center gap-1.5 md:gap-3 ml-auto flex-shrink-0" data-id="element-2873">
          <LocationSelector onOverrideChange={setLocationOverride} data-id="element-2874" />
          <div className="h-5 w-px bg-slate-700" data-id="element-2875" />
          <ModelRunSelector onOverrideChange={setModelOverride} data-id="element-2876" />
          <div className="h-5 w-px bg-slate-700 hidden sm:block" data-id="element-2877" />
          <div className="hidden sm:flex items-center gap-2 text-xs font-medium transition-opacity duration-300" style={{
          opacity: saveStatus === 'idle' ? 0.7 : 1
        }} data-id="element-2878">
            {saveStatus === 'saving' && <>
                <Cloud size={14} className="text-white/70 animate-pulse" data-id="element-2879" />
                <span className="text-white/70" data-id="element-2880">Saving…</span>
              </>}
            {saveStatus === 'saved' && <>
                <Check size={14} className="text-emerald-400" data-id="element-2881" />
                <span className="text-emerald-400" data-id="element-2882">Saved</span>
              </>}
            {saveStatus === 'idle' && <>
                <Cloud size={14} className="text-slate-400" data-id="element-2883" />
                <span className="text-slate-400" data-id="element-2884">Auto-saved</span>
              </>}
          </div>
          <div className="sm:hidden flex items-center transition-opacity duration-300" style={{
          opacity: saveStatus === 'idle' ? 0.5 : 1
        }} title={saveStatus === 'saving' ? 'Saving…' : saveStatus === 'saved' ? 'Saved' : 'Auto-saved'} data-id="element-2885">
            {saveStatus === 'saving' && <Cloud size={14} className="text-white/70 animate-pulse" data-id="element-2886" />}
            {saveStatus === 'saved' && <Check size={14} className="text-emerald-400" data-id="element-2887" />}
            {saveStatus === 'idle' && <Cloud size={14} className="text-slate-400" data-id="element-2888" />}
          </div>
        </div>
      </div>

      {/* Grid Canvas */}
      <div className="flex-1 overflow-y-auto p-2 md:p-6 relative" data-id="element-2889">
        {backgroundMapId && widgets.find(w => w.i === backgroundMapId && w.type === 'map') && <div className="absolute inset-0 z-0" data-id="element-2890">
              <MapWidget id={backgroundMapId} onRemove={() => {
          setBackgroundMapId(null);
          removeWidget(backgroundMapId);
        }} onDuplicate={() => duplicateWidget(backgroundMapId)} onToggleBackground={() => setBackgroundMapId(null)} isBackground={true} onParameterChange={triggerSave} modelOverride={modelOverride} locationOverride={locationOverride} data-id="element-2891" />
            </div>}

        <div className={backgroundMapId ? 'relative z-10' : ''} data-id="element-2892">
          <ResponsiveGridLayout className="layout" layouts={{
          xl: widgets.filter(w => w.i !== backgroundMapId).map(w => ({
            i: w.i,
            x: w.x,
            y: w.y,
            w: w.w,
            h: w.h
          })),
          lg: widgets.filter(w => w.i !== backgroundMapId).map(w => ({
            i: w.i,
            x: w.x,
            y: w.y,
            w: Math.min(w.w, 12),
            h: w.h
          })),
          md: widgets.filter(w => w.i !== backgroundMapId).map(w => ({
            i: w.i,
            x: w.x,
            y: w.y,
            w: Math.min(w.w, 12),
            h: w.h
          })),
          sm: widgets.filter(w => w.i !== backgroundMapId).map((w, i) => ({
            i: w.i,
            w: 12,
            x: 0,
            y: i * 4,
            h: w.h
          })),
          xs: widgets.filter(w => w.i !== backgroundMapId).map((w, i) => ({
            i: w.i,
            w: 12,
            x: 0,
            y: i * 4,
            h: w.h
          })),
          xxs: widgets.filter(w => w.i !== backgroundMapId).map((w, i) => ({
            i: w.i,
            w: 12,
            x: 0,
            y: i * 4,
            h: w.h
          }))
        }} breakpoints={{
          xl: 1600,
          lg: 1200,
          md: 900,
          sm: 600,
          xs: 480,
          xxs: 0
        }} cols={{
          xl: 12,
          lg: 12,
          md: 12,
          sm: 12,
          xs: 12,
          xxs: 12
        }} rowHeight={100} draggableHandle=".draggable-handle" draggableCancel="button, input, select, textarea, a, [data-no-drag], .recharts-wrapper, .leaflet-container" margin={[24, 24]} onLayoutChange={handleLayoutChange} compactType="vertical" data-id="element-2893">
            {widgets.filter(w => w.i !== backgroundMapId).map(widget => <div key={widget.i} data-id="element-2894">
                  {widget.type === 'chart' ? <ChartWidget id={widget.i} onRemove={() => removeWidget(widget.i)} onDuplicate={() => duplicateWidget(widget.i)} onParameterChange={triggerSave} onDoubleClickHandle={() => expandWidget(widget.i)} modelOverride={modelOverride} locationOverride={locationOverride} data-id="element-2895" /> : widget.type === 'map' ? <MapWidget id={widget.i} onRemove={() => removeWidget(widget.i)} onDuplicate={() => duplicateWidget(widget.i)} onToggleBackground={() => setBackgroundMapId(widget.i)} isBackground={false} onParameterChange={triggerSave} onDoubleClickHandle={() => expandWidget(widget.i)} modelOverride={modelOverride} locationOverride={locationOverride} initialOverlay={widget.config?.overlay} initialPOIs={widget.config?.pois} data-id="element-2896" /> : widget.type === 'text' ? <TextWidget id={widget.i} onRemove={() => removeWidget(widget.i)} onDuplicate={() => duplicateWidget(widget.i)} onParameterChange={triggerSave} onDoubleClickHandle={() => expandWidget(widget.i)} initialPrompt={widget.config?.prompt} data-id="element-2897" /> : widget.type === 'table' ? <TableWidget id={widget.i} onRemove={() => removeWidget(widget.i)} onDuplicate={() => duplicateWidget(widget.i)} onParameterChange={triggerSave} onDoubleClickHandle={() => expandWidget(widget.i)} modelOverride={modelOverride} locationOverride={locationOverride} data-id="element-2898" /> : null}
                </div>)}
          </ResponsiveGridLayout>
        </div>
      </div>
    </div>;
}